import React, { useEffect, useMemo, useState } from "react";
import { MainLayout } from "../layout/mainLayout";
import { showApiError } from "../framework/formUtils";
import { WorkShiftTimelineGrid } from "./WorkShiftTimelineGrid";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { WorkShiftPlanningTopBar } from "./WorkShiftPlanningTopBar";
import Box from "@mui/material/Box";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { useVerifyOptionEnabled } from "../../hooks/useAppOption";
import { OptionRoutesEnabled } from "../../models/common/consts";
import { useParams } from "react-router-dom";
import {
    WorkShiftPlanningParams,
    WorkShiftPlanningTab,
} from "./WorkShiftPlanningRouter";
import { useAsyncThunkAction } from "../../hooks/useAsyncThunkAction";
import {
    fetchPlannedWorkShifts,
    fetchDayBookingTypes,
    fetchDayBookings,
    clearDayBookings,
} from "../../store/workShiftPlanningSlice";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { getEmployees } from "../../services/employeeServiceNew";
import {
    setEmployees as setEmployeesAction,
    setGroupEmployees as setGroupEmployeesAction,
} from "../../store/employeeSlice";
import { fetchColorScheme } from "../../store/colorSchemeSlice";
import { WorkShiftPlanningSidebar } from "./sidebar/WorkShiftPlanningSidebar";

export const WorkShiftPlanningMain = () => {
    const dispatch = useAppDispatch();
    const { tab } = useParams<WorkShiftPlanningParams>();
    const [employees, setEmployees] = useState<EmployeeListItemDto[]>();
    const [allEmployees, setAllEmployees] = useState<EmployeeListItemDto[]>();
    const selectedEmployeeGroupId = useAppSelector(
        (state) => state.workShiftPlanning.filters.selectedEmployeeGroupId
    );
    const selectedEmployeeIds = useAppSelector(
        (state) => state.workShiftPlanning.filters.selectedEmployeeIds
    );
    const selectedVehicleGroupId = useAppSelector(
        (state) => state.workShiftPlanning.filters.selectedVehicleGroupId
    );
    const includeUnassigned = useAppSelector(
        (state) => state.workShiftPlanning.filters.showUnemployable
    );
    const sidebarShiftId = useAppSelector(
        (state) => state.workShiftPlanning.sidebar.plannedShiftId
    );
    const visibleEmployees = useMemo(
        () =>
            employees?.filter(
                (employee) =>
                    !selectedEmployeeIds ||
                    selectedEmployeeIds.length === 0 ||
                    selectedEmployeeIds.includes(employee.id)
            ),
        [selectedEmployeeIds, employees]
    );

    const visibleEmployeeIds = useMemo(
        () => visibleEmployees?.map((employee) => employee.id).filter(Boolean) ?? [],
        [visibleEmployees]
    );

    useVerifyOptionEnabled(
        OptionRoutesEnabled,
        tab === WorkShiftPlanningTab.Route
    );

    const shifts = useAppSelector(
        (state) => state.workShiftPlanning.plannedWorkShifts
    );
    const loading = useAppSelector(
        (state) => state.workShiftPlanning.shiftsLoading
    );
    const reloadKey = useAppSelector(
        (state) => state.workShiftPlanning.reloadKey
    );
    const { startDate, endDate } = useAppSelector(
        (state) => state.workShiftPlanning.filters
    );

    useEffect(() => {
        getEmployees(null)
            .then((res) => {
                setAllEmployees(res?.data ?? []);
                if (res?.data) dispatch(setEmployeesAction(res.data));
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        getEmployees(selectedEmployeeGroupId)
            .then((res) => {
                setEmployees(res?.data ?? []);
                if (res?.data) dispatch(setGroupEmployeesAction({ groupId: selectedEmployeeGroupId, employees: res.data }));
            })
            .catch(showApiError);
    }, [selectedEmployeeGroupId]);

    useAsyncThunkAction(
        () => {
            if (!!startDate && !!endDate) {
                return fetchPlannedWorkShifts({
                    startDate,
                    endDate,
                    employeeGroupId: selectedEmployeeGroupId || undefined,
                    employeeIds:
                        selectedEmployeeIds && selectedEmployeeIds.length > 0
                            ? selectedEmployeeIds
                            : undefined,
                    vehicleGroupId: selectedVehicleGroupId || undefined,
                    includeUnassigned,
                });
            }
        },
        { onError: showApiError },
        [
            reloadKey,
            startDate,
            endDate,
            selectedEmployeeGroupId,
            selectedEmployeeIds,
            selectedVehicleGroupId,
            includeUnassigned,
        ]
    );

    useEffect(() => {
        dispatch(fetchColorScheme("workShiftPlanning"));
    }, [dispatch]);

    useAsyncThunkAction(
        () => {
            if (tab === WorkShiftPlanningTab.Employee) {
                return fetchDayBookingTypes();
            }
            return null;
        },
        { onError: showApiError },
        [tab]
    );

    useAsyncThunkAction(
        () => {
            if (
                tab === WorkShiftPlanningTab.Employee &&
                startDate &&
                endDate &&
                visibleEmployeeIds.length > 0
            ) {
                return fetchDayBookings({
                    startDate,
                    endDate,
                    employeeIds: visibleEmployeeIds,
                });
            }
            return null;
        },
        { onError: showApiError },
        [tab, startDate, endDate, visibleEmployeeIds]
    );

    useEffect(() => {
        if (tab !== WorkShiftPlanningTab.Employee) {
            dispatch(clearDayBookings());
            return;
        }

        if (!startDate || !endDate) {
            dispatch(clearDayBookings());
            return;
        }

        if (visibleEmployeeIds.length === 0) {
            dispatch(clearDayBookings());
        }
    }, [tab, startDate, endDate, visibleEmployeeIds.length, dispatch]);

    return (
        <MainLayout
            topComponent={
                <WorkShiftPlanningTopBar
                    employees={allEmployees}
                    visibleEmployees={visibleEmployees}
                />
            }
            sideComponent={
                !!sidebarShiftId ? <WorkShiftPlanningSidebar /> : null
            }
            sideComponentNoPadding
        >
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <LoadingIndicator loading={loading} />
                {!!startDate && !!endDate ? (
                    <WorkShiftTimelineGrid
                        shifts={shifts}
                        timelineStart={startDate}
                        timelineEnd={endDate}
                        employees={visibleEmployees}
                    />
                ) : null}
            </Box>
        </MainLayout>
    );
};
